import axios, { AxiosError, AxiosInstance } from 'axios';
import type {
  AuthTokenResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UserCreate,
  UserUpdate,
  ChangePasswordRequest,
  Entity,
  GroupAccount,
  EntityAccount,
  TrialBalance,
  TrialBalanceSummary,
  TrialBalanceUpload,
  ConsolidationRun,
  ConsolidationResult,
  JournalEntry,
  FinancialStatement,
  FinancialLineItem,
  NciReport,
  NciPipelineResult,
  Currency,
  FxRate,
  Period,
  IntercompanyTransaction,
  IntercompanySummary,
  IntercompanyReconciliationReport,
  IntercompanyEliminationTestResult,
  EntityCodeMapping,
  IcDispute,
  ValidateCodesResponse,
  PaginatedResponse,
  ApiError,
  SkillExecution,
  AuditLog,
  AuditSummary,
  TaxComputeResponse,
  EntityTaxDetail,
  QfzpEntityStatus,
  TPTransaction,
  TPSummary,
  Anomaly,
  AnomalySummary,
  BenfordAnalysis,
  ExportRequest,
  ERPConnector,
  Notification,
  ApprovalWorkflow,
  NLQueryResponse,
  XBRLMapping,
  XBRLValidationResult,
  BackgroundTask,
  SSOProvider,
  MFASetup,
  TaxReturn,
  ESGMetric,
  ESGDashboardData,
  ESGTarget,
  ForecastResult,
  GaapConversion,
  MarketplaceSkill,
  AuditBlock,
  AuditChainStats,
  ScenarioResult,
  TrialBalanceTranslationResponse,
  TranslationRequest,
  ConsolidationReviewMatrix,
  ConsolidationReviewSummary,
} from '@/types';
import { SkillStatus } from '@/types';

// --- Axios Instance ----------------------------------------------------------

/** Resolve API base URL. Bare absolute origins (e.g. https://app.example.com) become …/api/v1 so POSTs hit the API, not the SPA (which often returns 405 for POST). */
function resolveApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  const fallback = '/api/v1';
  if (raw == null || String(raw).trim() === '') {
    return fallback;
  }
  let s = String(raw).trim().replace(/\/$/, '');
  if (s.startsWith('/')) {
    if (s === '/api') {
      return fallback;
    }
    return s;
  }
  try {
    const u = new URL(s);
    const p = (u.pathname || '').replace(/\/$/, '') || '';
    if (p === '' || p === '/') {
      u.pathname = '/api/v1';
      return u.toString().replace(/\/$/, '');
    }
    return s;
  } catch {
    return s;
  }
}

const BASE_URL = resolveApiBaseUrl();

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: attach JWT
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle errors with auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

function processQueue(error: any, token: string | null) {
  failedQueue.forEach((p) => {
    if (token) p.resolve(token);
    else p.reject(error);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as any;

    // Try to refresh token on 401 (but not for login/refresh endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(apiClient(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await apiClient.post<AuthTokenResponse>('/auth/refresh', {
            refresh_token: refreshToken,
          });
          const newToken = response.data.access_token;
          localStorage.setItem('auth_token', newToken);
          if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
          }
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed — log out
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token — log out
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    const apiError: ApiError = {
      detail: error.response?.data?.detail || error.message || 'An unexpected error occurred',
      status_code: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };

    return Promise.reject(apiError);
  },
);

// --- Auth API ----------------------------------------------------------------

export const authApi = {
  login: async (data: LoginRequest): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/register', data);
    return response.data;
  },

  getMe: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  refreshToken: async (): Promise<AuthTokenResponse> => {
    const response = await apiClient.post<AuthTokenResponse>('/auth/refresh');
    return response.data;
  },
};

// --- Entities API ------------------------------------------------------------

// Backend uses `parent_entity_id` (int); frontend Entity uses `parent_id` (string).
// Translate at the API boundary so the rest of the UI stays clean.
function normalizeEntity(raw: Record<string, any>): Entity {
  return {
    ...raw,
    id: String(raw.id),
    parent_id: raw.parent_entity_id != null ? String(raw.parent_entity_id) : null,
    ownership_pct: Number(raw.ownership_pct),
  } as Entity;
}

function toEntityPayload(data: Partial<Entity>): Record<string, any> {
  const { parent_id, ...rest } = data as any;
  return {
    ...rest,
    parent_entity_id: parent_id ? Number(parent_id) : null,
  };
}

export const entitiesApi = {
  list: async (params?: { page?: number; page_size?: number; search?: string; exclude_icp?: boolean; only_icp?: boolean; is_active?: boolean | null }): Promise<PaginatedResponse<Entity>> => {
    const finalParams = { is_active: true, exclude_icp: true, ...params };
    const response = await apiClient.get<PaginatedResponse<any>>('/entities', { params: finalParams });
    const raw: any[] = response.data.items;
    const idToName: Record<number, string> = {};
    raw.forEach((e) => { idToName[e.id] = e.name; });
    return {
      ...response.data,
      items: raw.map((e) => ({
        ...normalizeEntity(e),
        parent_name: e.parent_entity_id != null ? idToName[e.parent_entity_id] : undefined,
      })),
    };
  },

  create: async (data: Partial<Entity>): Promise<Entity> => {
    const response = await apiClient.post<any>('/entities', toEntityPayload(data));
    return normalizeEntity(response.data);
  },

  get: async (id: string): Promise<Entity> => {
    const response = await apiClient.get<any>(`/entities/${id}`);
    return normalizeEntity(response.data);
  },

  update: async (id: string, data: Partial<Entity>): Promise<Entity> => {
    const response = await apiClient.put<any>(`/entities/${id}`, toEntityPayload(data));
    return normalizeEntity(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/entities/${id}`);
  },

  hierarchy: async (): Promise<Entity[]> => {
    const response = await apiClient.get<any[]>('/entities/hierarchy');
    return response.data.map(normalizeEntity);
  },

  bulkEnableIntercompany: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/entities/bulk-enable-intercompany'
    );
    return response.data;
  },
};

// --- Accounts API ------------------------------------------------------------

export const accountsApi = {
  listGroup: async (params?: { account_type?: string; search?: string }): Promise<GroupAccount[]> => {
    const response = await apiClient.get<any>('/accounts/group', { params: { page_size: 2000, ...params } });
    return response.data?.items ?? response.data;
  },

  createGroup: async (data: Partial<GroupAccount>): Promise<GroupAccount> => {
    const response = await apiClient.post<GroupAccount>('/accounts/group', data);
    return response.data;
  },

  updateGroup: async (id: string, data: Partial<GroupAccount>): Promise<GroupAccount> => {
    const response = await apiClient.put<GroupAccount>(`/accounts/group/${id}`, data);
    return response.data;
  },

  deleteGroup: async (id: string): Promise<void> => {
    await apiClient.delete(`/accounts/group/${id}`);
  },

  syncIcpFlags: async (): Promise<{ updated: number; icp_count: number; source: string; message: string }> => {
    const response = await apiClient.post<{ updated: number; icp_count: number; source: string; message: string }>('/accounts/sync-icp-flags');
    return response.data;
  },

  listEntity: async (entityId: string, params?: { mapped?: boolean; search?: string }): Promise<EntityAccount[]> => {
    const response = await apiClient.get<any>('/accounts/entity', { params: { entity_id: entityId, page_size: 500, ...params } });
    return response.data?.items ?? response.data;
  },

  createEntity: async (entityId: string, data: Partial<EntityAccount>): Promise<EntityAccount> => {
    const response = await apiClient.post<EntityAccount>(`/accounts/entity/${entityId}`, data);
    return response.data;
  },

  mapAccount: async (entityAccountId: string, groupAccountId: string): Promise<EntityAccount> => {
    const response = await apiClient.post<EntityAccount>('/accounts/map', {
      entity_account_id: Number(entityAccountId),
      group_account_id: Number(groupAccountId),
    });
    return response.data;
  },

  unmapAccount: async (entityAccountId: string): Promise<EntityAccount> => {
    const response = await apiClient.put<EntityAccount>(`/accounts/entity/${entityAccountId}/unmap`);
    return response.data;
  },

  listUnmapped: async (entityId: string): Promise<EntityAccount[]> => {
    const response = await apiClient.get<any>('/accounts/entity', { params: { entity_id: entityId, mapping_status: 'unmapped', page_size: 500 } });
    return response.data?.items ?? response.data;
  },

  uploadMapping: async (entityId: string, file: File): Promise<{ rows_processed: number; mapped: number; skipped: number; warnings: string[]; errors: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/accounts/entity/upload-mapping?entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadCOA: async (entityId: string, file: File): Promise<{
    rows_processed: number; created: number; updated: number; skipped: number;
    warnings: string[]; errors: string[];
    ai_column_detection?: boolean; detected_code_column?: string; detected_name_column?: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/accounts/entity/upload-coa?entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  coaSummary: async (): Promise<{
    entity_id: number; entity_code: string; entity_name: string; functional_currency: string;
    total_accounts: number; mapped_accounts: number; unmapped_accounts: number; mapping_pct: number;
  }[]> => {
    const response = await apiClient.get('/accounts/entity/coa-summary');
    return response.data;
  },

  aiMap: async (entityId: string): Promise<{
    total_processed: number; auto_mapped: number; skipped: number; errors: string[];
  }> => {
    const response = await apiClient.post(`/accounts/entity/ai-map?entity_id=${entityId}`);
    return response.data;
  },

  downloadCoaTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/accounts/entity/coa-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'entity_coa_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  uploadBulkMapping: async (file: File): Promise<{
    total_mapped: number; total_skipped: number; entities_processed: number;
    entity_results: { entity_code: string; entity_name: string; sheet: string | null; explicit_mapped: number; wildcard_mapped: number; skipped: number; status: string }[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/accounts/entity/upload-bulk-mapping', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadGroupCoa: async (file: File): Promise<{
    rows_processed: number; created: number; updated: number; skipped: number;
    warnings: string[]; errors: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/accounts/group/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadGroupCoaTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/accounts/group/coa-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'group_coa_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  copyFromGroup: async (entityId: string): Promise<{
    created: number; skipped: number; total_group_accounts: number;
  }> => {
    const response = await apiClient.post(`/accounts/entity/${entityId}/copy-from-group`);
    return response.data;
  },
};

// --- Trial Balances API ------------------------------------------------------

export const trialBalancesApi = {
  upload: async (
    entityId: string,
    periodId: string,
    file: File,
    scenario: string = 'Actual',
    balanceType: string = 'ytd',
  ): Promise<{ rows_imported: number; errors: string[]; warnings: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ rows_imported: number; errors: string[]; warnings: string[] }>(
      `/trial-balances/upload?entity_id=${entityId}&period_id=${periodId}&scenario=${encodeURIComponent(scenario)}&balance_type=${encodeURIComponent(balanceType)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  uploadBulk: async (
    entityId: string,
    year: number,
    files: File[],
    scenario: string = 'Actual',
    balanceType: string = 'monthly',
  ): Promise<{
    total_files: number;
    files_processed: number;
    files_skipped: number;
    files_errored: number;
    total_rows_imported: number;
    file_results: Array<{ filename: string; status: string; period?: string; rows_imported: number; reason?: string; warnings?: string[]; errors?: string[] }>;
    errors: string[];
    warnings: string[];
  }> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await apiClient.post(
      `/trial-balances/upload-bulk?entity_id=${entityId}&year=${year}&scenario=${encodeURIComponent(scenario)}&balance_type=${encodeURIComponent(balanceType)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  uploadOpeningBalances: async (
    entityId: string,
    periodId: string,
    file: File,
  ): Promise<{ rows_imported: number; errors: string[]; warnings: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ rows_imported: number; errors: string[]; warnings: string[] }>(
      `/trial-balances/upload-opening-balances?entity_id=${entityId}&period_id=${periodId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  listUploads: async (params?: {
    entity_id?: string;
    period_id?: string;
    scenario?: string;
    balance_type?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalanceUpload>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalanceUpload>>('/trial-balances/uploads', { params });
    return response.data;
  },

  updateBalanceType: async (params: {
    entity_id: number;
    period_id: number;
    balance_type: 'ytd' | 'monthly';
    scenario?: string;
    filename?: string | null;
  }): Promise<{ status: string; message: string }> => {
    const response = await apiClient.patch('/trial-balances/uploads/balance-type', null, { params });
    return response.data;
  },

  list: async (params?: {
    entity_id?: string;
    period_id?: string;
    member?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalance>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalance>>('/trial-balances', { params });
    return response.data;
  },

  adjusted: async (params: {
    entity_id: string;
    period_id: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<TrialBalance>> => {
    const response = await apiClient.get<PaginatedResponse<TrialBalance>>('/trial-balances/adjusted', { params });
    return response.data;
  },

  summary: async (entityId: string, periodId: string): Promise<TrialBalanceSummary> => {
    const response = await apiClient.get<TrialBalanceSummary>('/trial-balances/summary', {
      params: { entity_id: entityId, period_id: periodId },
    });
    return response.data;
  },

  validate: async (entityId: string, periodId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/trial-balances/validate?entity_id=${entityId}&period_id=${periodId}`,
    );
    return response.data;
  },

  downloadTemplate: async (entityId: string): Promise<Blob> => {
    const response = await apiClient.get('/trial-balances/sample-template', {
      params: { entity_id: entityId },
      responseType: 'blob',
    });
    return response.data;
  },

  getStatement: async (
    entityId: string,
    periodId: string,
    statementType: 'balance_sheet' | 'income_statement',
    toCurrency?: string,
  ) => {
    const response = await apiClient.get('/trial-balances/entity-statement', {
      params: {
        entity_id: entityId,
        period_id: periodId,
        statement_type: statementType,
        ...(toCurrency ? { to_currency: toCurrency } : {}),
      },
    });
    return response.data;
  },

  exportStatement: async (
    entityId: string,
    periodId: string,
    statementType: 'balance_sheet' | 'income_statement',
    opts?: {
      level?: number;
      to_currency?: string;
      export_original?: boolean;
      export_translated?: boolean;
    },
  ): Promise<Blob> => {
    const params: Record<string, string | number | boolean> = {
      entity_id: entityId,
      period_id: periodId,
      statement_type: statementType,
    };
    if (opts?.level !== undefined) params.level = opts.level;
    if (opts?.to_currency) params.to_currency = opts.to_currency;
    if (opts?.export_original !== undefined) params.export_original = opts.export_original;
    if (opts?.export_translated !== undefined) params.export_translated = opts.export_translated;
    const response = await apiClient.get('/trial-balances/entity-statement/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  deleteUpload: async (entityId: number, periodId: number, scenario: string = 'Actual'): Promise<{ status: string; message: string }> => {
    const response = await apiClient.delete<{ status: string; message: string }>('/trial-balances/upload', {
      params: { entity_id: entityId, period_id: periodId, scenario },
    });
    return response.data;
  },
  translate: async (params: {
    entity_id: string;
    period_id: string;
    to_currency: string;
  }): Promise<TrialBalanceTranslationResponse> => {
    const response = await apiClient.post<TrialBalanceTranslationResponse>(
      `/trial-balances/translate`,
      null,
      { params },
    );
    return response.data;
  },

  calculateNci: async (entityId: string, periodId: string): Promise<any> => {
    const response = await apiClient.post<any>(
      `/trial-balances/calculate-nci`,
      null,
      { params: { entity_id: entityId, period_id: periodId } }
    );
    return response.data;
  },

  /**
   * Run the full automated NCI pipeline (translate if needed → CTA → NCI).
   * entity_id and period_id are required. reporting_currency defaults to AED.
   */
  nciPipeline: async (params: {
    entity_id: string;
    period_id: string;
    reporting_currency?: string;
  }): Promise<NciPipelineResult> => {
    const response = await apiClient.post<NciPipelineResult>(
      `/trial-balances/nci-pipeline`,
      null,
      { params: { ...params, reporting_currency: params.reporting_currency ?? 'AED' } },
    );
    return response.data;
  },
};


// --- Consolidation API -------------------------------------------------------

const CONSOLIDATION_PIPELINE: readonly string[] = [
  'SKILL-001',
  'SKILL-002',
  'SKILL-003',
  'SKILL-004',
  'SKILL-008',
];

const CONSOLIDATION_SKILL_MAP: Record<string, string> = {
  'SKILL-001': 'data_validation',
  'SKILL-002': 'ic_elimination',
  'SKILL-003': 'fx_translation',
  'SKILL-004': 'equity_consolidation',
  'SKILL-008': 'generate_statements',
};

const CONSOLIDATION_SKILL_LABELS: Record<string, string> = {
  'SKILL-001': 'Data Validation',
  'SKILL-002': 'IC Elimination',
  'SKILL-003': 'FX Translation',
  'SKILL-004': 'Equity Consolidation',
  'SKILL-008': 'Generate Statements',
};

const CONSOLIDATION_SKILL_DESC: Record<string, string> = {
  'SKILL-001': 'Validate trial balances, mappings, and FX rates',
  'SKILL-002': 'Eliminate intercompany transactions and balances',
  'SKILL-003': 'Translate foreign currency balances to reporting currency',
  'SKILL-004': 'Consolidate equity and calculate minority interest',
  'SKILL-008': 'Generate consolidated financial statements',
};

function buildConsolidationSkillsFromSummary(data: {
  status?: string;
  error_message?: string | null;
  results_summary?: Record<string, unknown> | null;
}, journalCountsBySkill: Record<string, number> = {}): SkillExecution[] {
  const rs = (data.results_summary || {}) as {
    skills_executed?: Array<{
      skill_id: string;
      skill_name?: string;
      status?: string;
      records_processed?: number;
      journal_entries_created?: number;
      summary?: { elimination_jes_created?: number };
    }>;
    skills_failed?: Array<{ skill_id: string; skill_name?: string; errors?: string[] }>;
  };
  const executed = rs.skills_executed || [];
  const failed = rs.skills_failed || [];
  const execById = Object.fromEntries(executed.map((s) => [s.skill_id, s]));
  const failById = Object.fromEntries(failed.map((s) => [s.skill_id, s]));
  const firstFailIdx = CONSOLIDATION_PIPELINE.findIndex((id) => failById[id]);

  return CONSOLIDATION_PIPELINE.map((skillId, i) => {
    const ex = execById[skillId];
    const fl = failById[skillId];
    const base = {
      skill_name: CONSOLIDATION_SKILL_MAP[skillId] || skillId,
      display_name: CONSOLIDATION_SKILL_LABELS[skillId] || ex?.skill_name || fl?.skill_name || skillId,
      description: CONSOLIDATION_SKILL_DESC[skillId] || '',
      step_order: i + 1,
      progress_pct: 0,
      message: 'Waiting to start',
      status: SkillStatus.PENDING,
    };

    if (ex) {
      const journalCount =
        (skillId === 'SKILL-002'
          ? (Number(ex.journal_entries_created) || Number(journalCountsBySkill[skillId]) || 0)
          : Number(ex.journal_entries_created) || 0);
      return {
        ...base,
        status: SkillStatus.COMPLETED,
        progress_pct: 100,
        message: `${ex.records_processed ?? 0} records, ${journalCount} JEs`,
      };
    }
    if (fl) {
      const err =
        Array.isArray(fl.errors) && fl.errors.length
          ? fl.errors.join('; ')
          : data.error_message || 'Failed';
      return {
        ...base,
        status: SkillStatus.FAILED,
        message: err.length > 240 ? `${err.slice(0, 240)}…` : err,
      };
    }
    if (firstFailIdx >= 0 && i > firstFailIdx) {
      return {
        ...base,
        status: SkillStatus.SKIPPED,
        message: 'Not run (pipeline stopped)',
      };
    }
    if (data.status === 'failed' && executed.length === 0 && failed.length === 0) {
      return {
        ...base,
        status: i === 0 ? SkillStatus.FAILED : SkillStatus.SKIPPED,
        message:
          i === 0
            ? data.error_message || 'Consolidation failed before skill results were recorded'
            : 'Not run',
      };
    }
    return base;
  });
}

export const consolidationApi = {
  /** Start a consolidation run. Pass ``parameters.entity_ids`` to scope to one or more entities (omit for full group). */
  run: async (
    periodId: string,
    options?: { parameters?: Record<string, unknown> }
  ): Promise<ConsolidationRun> => {
    const body: Record<string, unknown> = { period_id: Number(periodId) };
    if (options?.parameters && Object.keys(options.parameters).length > 0) {
      body.parameters = options.parameters;
    }
    const response = await apiClient.post<ConsolidationRun>('/consolidations/run', body);
    return response.data;
  },

  list: async (params?: { period_id?: string; status?: string }): Promise<ConsolidationRun[]> => {
    const response = await apiClient.get<any>('/consolidations', { params });
    return response.data?.items ?? response.data;
  },

  get: async (runId: string): Promise<ConsolidationResult> => {
    const [response, journalEntriesResponse] = await Promise.all([
      apiClient.get<any>(`/consolidations/${runId}`),
      apiClient.get<JournalEntry[]>(`/consolidations/${runId}/journal-entries`),
    ]);
    const data = response.data;
    const journalEntries = journalEntriesResponse.data || [];
    const runReference = `consolidation_run:${runId}`;
    const runScopedJournalEntries = journalEntries.filter((je) => je.reference === runReference);
    const rs = (data.results_summary || {}) as Record<string, unknown>;
    const skillsExecuted = (rs.skills_executed || []) as Array<{
      skill_id: string;
      journal_entries_created?: number;
      summary?: { elimination_jes_created?: number; total_eliminated_amount?: string | number };
    }>;
    const journalCountsBySkill = runScopedJournalEntries.reduce<Record<string, number>>((acc, je) => {
      const skillId = je.skill_name || '';
      if (!skillId) return acc;
      acc[skillId] = (acc[skillId] || 0) + 1;
      return acc;
    }, {});
    if (!journalCountsBySkill['SKILL-002']) {
      journalCountsBySkill['SKILL-002'] = journalEntries.filter((je) => je.skill_name === 'SKILL-002').length;
    }

    const skills = buildConsolidationSkillsFromSummary(data, journalCountsBySkill);

    const skill002 = skillsExecuted.find((s) => s.skill_id === 'SKILL-002');
    const skill003 = skillsExecuted.find((s) => s.skill_id === 'SKILL-003');
    const skill004 = skillsExecuted.find((s) => s.skill_id === 'SKILL-004');

    const actualElimJes = (
      runScopedJournalEntries.length > 0
        ? runScopedJournalEntries.filter((je) => je.skill_name === 'SKILL-002')
        : journalEntries.filter((je) => je.skill_name === 'SKILL-002')
    );

    const eliminationAmountFromJournals = actualElimJes.reduce((sum, je) => (
      sum + je.lines.reduce((lineSum, line) => lineSum + (Number(line.debit_amount) || 0), 0)
    ), 0);

    const elimJes =
      actualElimJes.length ||
      Number(skill002?.journal_entries_created) ||
      Number(skill002?.summary?.elimination_jes_created) ||
      0;
    const eliminationAmount =
      eliminationAmountFromJournals ||
      Number(skill002?.summary?.total_eliminated_amount) ||
      0;
    const fxJes = Number(skill003?.journal_entries_created) || 0;
    const equityJes = Number(skill004?.journal_entries_created) || 0;

    return {
      run_id: String(data.id),
      status: data.status,
      skills,
      journal_entries: journalEntries,
      summary: {
        total_eliminations: elimJes,
        elimination_amount: eliminationAmount,
        fx_adjustments: fxJes,
        equity_adjustments: equityJes,
        total_journal_entries: Number(rs.total_journal_entries_created) || 0,
      },
    };
  },

  results: async (runId: string): Promise<ConsolidationResult> => {
    const response = await apiClient.get<ConsolidationResult>(`/consolidations/${runId}/results`);
    return response.data;
  },

  journalEntries: async (runId: string): Promise<JournalEntry[]> => {
    const response = await apiClient.get<JournalEntry[]>(`/consolidations/${runId}/journal-entries`);
    return response.data;
  },

  approve: async (runId: string): Promise<ConsolidationRun> => {
    const response = await apiClient.post<ConsolidationRun>(`/consolidations/${runId}/approve`);
    return response.data;
  },

  rollback: async (runId: string): Promise<ConsolidationRun> => {
    const response = await apiClient.post<ConsolidationRun>(`/consolidations/${runId}/rollback`);
    return response.data;
  },

  executeSkill: async (runId: string, skillName: string, params?: Record<string, unknown>): Promise<SkillExecution> => {
    const response = await apiClient.post<SkillExecution>(`/consolidations/${runId}/skills/${skillName}`, params);
    return response.data;
  },
};

// --- Reports API -------------------------------------------------------------

// Transform backend report response (sections-based) to frontend FinancialStatement (line_items-based)
function transformReportResponse(data: any): FinancialStatement {
  // If already has line_items, return as-is
  if (data.line_items) return data as FinancialStatement;

  const sectionConfig: Record<string, { label: string; accountType: string }> = {
    assets: { label: 'Assets', accountType: 'asset' },
    liabilities: { label: 'Liabilities', accountType: 'liability' },
    equity: { label: 'Equity', accountType: 'equity' },
    revenue: { label: 'Revenue', accountType: 'revenue' },
    expenses: { label: 'Expenses', accountType: 'expense' },
    operating_activities: { label: 'Operating Activities', accountType: 'expense' },
    investing_activities: { label: 'Investing Activities', accountType: 'asset' },
    financing_activities: { label: 'Financing Activities', accountType: 'liability' },
    cash_and_equivalents: { label: 'Cash and Equivalents', accountType: 'asset' },
  };

  const lineItems: FinancialLineItem[] = [];
  const sections = data.sections || {};

  for (const [sectionKey, items] of Object.entries(sections)) {
    const config = sectionConfig[sectionKey] || { label: sectionKey, accountType: 'asset' };
    const sectionItems = items as any[];
    if (!sectionItems || sectionItems.length === 0) continue;

    // Section header
    lineItems.push({
      account_code: '',
      account_name: config.label,
      account_type: config.accountType as any,
      is_header: true,
      is_total: false,
      indent_level: 0,
      current_period: 0,
      prior_period: null,
      variance: null,
      variance_pct: null,
    });

    // Section items — group by parent_name for hierarchy
    let lastParent: string | null = null;
    for (const item of sectionItems) {
      const parentName = item.parent_name || null;
      if (parentName && parentName !== lastParent) {
        lineItems.push({
          account_code: item.parent_code || '',
          account_name: parentName,
          account_type: config.accountType as any,
          is_header: true,
          is_total: false,
          indent_level: 1,
          current_period: 0,
          prior_period: null,
          variance: null,
          variance_pct: null,
        });
        lastParent = parentName;
      }
      const balance = Number(item.consolidated_balance || item.balance || 0);
      lineItems.push({
        account_code: item.account_code || '',
        account_name: item.account_name || '',
        account_type: config.accountType as any,
        is_header: false,
        is_total: false,
        indent_level: parentName ? 2 : 1,
        current_period: balance,
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }

    // Section total
    const totalKey = `total_${sectionKey}`;
    const totalValue = data.totals?.[totalKey];
    if (totalValue !== undefined) {
      lineItems.push({
        account_code: '',
        account_name: `Total ${config.label}`,
        account_type: config.accountType as any,
        is_header: false,
        is_total: true,
        indent_level: 0,
        current_period: Number(totalValue),
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }
  }

  // Net income line for income statement
  if (data.net_income !== undefined) {
    lineItems.push({
      account_code: '',
      account_name: 'Net Income',
      account_type: 'revenue' as any,
      is_header: false,
      is_total: true,
      indent_level: 0,
      current_period: Number(data.net_income),
      prior_period: null,
      variance: null,
      variance_pct: null,
    });
  }

  // Unmapped accounts section — show entity accounts that have no group account mapping
  const unmappedAccounts = data.unmapped_accounts || [];
  if (unmappedAccounts.length > 0) {
    lineItems.push({
      account_code: '',
      account_name: `Unmapped Accounts (${unmappedAccounts.length})`,
      account_type: 'asset' as any,
      is_header: true,
      is_total: false,
      indent_level: 0,
      current_period: 0,
      prior_period: null,
      variance: null,
      variance_pct: null,
    });

    for (const ua of unmappedAccounts) {
      const balance = Number(ua.consolidated_balance || ua.balance || 0);
      lineItems.push({
        account_code: ua.account_code || '',
        account_name: ua.account_name || '',
        account_type: 'asset' as any,
        is_header: false,
        is_total: false,
        indent_level: 1,
        current_period: balance,
        prior_period: null,
        variance: null,
        variance_pct: null,
      });
    }

    lineItems.push({
      account_code: '',
      account_name: 'Total Unmapped',
      account_type: 'asset' as any,
      is_header: false,
      is_total: true,
      indent_level: 0,
      current_period: Number(data.unmapped_total || 0),
      prior_period: null,
      variance: null,
      variance_pct: null,
    });
  }

  return {
    statement_type: data.report_type === 'income_statement' ? 'income_statement' : data.report_type === 'cash_flow' ? 'cash_flow' : 'balance_sheet',
    period_name: data.period || 'Current',
    prior_period_name: null,
    entity_name: data.is_consolidated ? 'Consolidated' : '',
    currency: 'AED',
    generated_at: new Date().toISOString(),
    line_items: lineItems,
    totals: Object.fromEntries(
      Object.entries(data.totals || {}).map(([k, v]) => [k, Number(v)])
    ),
    warnings: data.warnings || [],
    unmapped_count: data.unmapped_count || 0,
    unmapped_total: Number(data.unmapped_total || 0),
    raw_unmapped_accounts: data.unmapped_accounts || [],
  };
}

export const reportsApi = {
  balanceSheet: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/balance-sheet', { params });
    return transformReportResponse(response.data);
  },

  incomeStatement: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/income-statement', { params });
    return transformReportResponse(response.data);
  },

  cashFlow: async (params: {
    period_id: string;
    entity_id?: string;
    comparative_period_id?: string;
    comparative?: boolean;
  }): Promise<FinancialStatement> => {
    const response = await apiClient.get<any>('/reports/cash-flow', { params });
    return transformReportResponse(response.data);
  },

  nciReport: async (params: {
    period_id: string;
    entity_id: string;
  }): Promise<NciReport> => {
    const response = await apiClient.get<NciReport>('/trial-balances/nci-report', { params });
    return response.data;
  },

  intercompanySummary: async (params: { period_id: string }): Promise<IntercompanySummary> => {
    const response = await apiClient.get<IntercompanySummary>('/reports/intercompany-summary', { params });
    return response.data;
  },

  export: async (params: {
    statement_type: string;
    period_id: string;
    format: 'excel' | 'pdf';
    entity_id?: string;
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  drillDown: async (params: {
    period_id: string;
    account_code: string;
    run_id?: string;
  }): Promise<{
    line_item_name: string;
    line_item_code: string;
    consolidated_amount: string | number;
    entity_breakdown: Array<{
      entity_id: number;
      entity_name: string;
      reported_balance: string | number;
      fx_adjustment: string | number;
      elimination_adjustment: string | number;
      equity_adjustment: string | number;
      tax_adjustment: string | number;
      consolidated_balance: string | number;
      currency: string;
      pct_of_total: string | number;
    }>;
  }> => {
    const response = await apiClient.get('/reports/drill-down', { params });
    return response.data;
  },

  monthlyDrilldown: async (params: {
    entity_id: string;
    account_code: string;
    period_id: string;
  }): Promise<{
    account_code: string;
    account_name: string;
    account_type: string;
    entity_name: string;
    target_period: string;
    currency: string;
    months: Array<{
      month: number;
      month_name: string;
      period_id: number;
      cumulative_ytd: number;
      monthly_amount: number;
    }>;
    ytd_total: number;
  }> => {
    const response = await apiClient.get('/reports/monthly-drilldown', { params });
    return response.data;
  },

  financialReports: async (params: {
    entity_id: number;
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
  }): Promise<any> => {
    const response = await apiClient.get('/reports/financial-reports', { params });
    return response.data;
  },

  financialReportsExport: async (params: {
    entity_id: number;
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
    report_type?: 'pnl' | 'balance_sheet' | 'both';
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/financial-reports/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  translate: async (data: TranslationRequest): Promise<any> => {
    const response = await apiClient.post('/reports/translate', data);
    return response.data;
  },

  financialReportsExportAll: async (params: {
    year: number;
    scenario?: string;
    view?: 'monthly' | 'quarterly' | 'annual';
    report_type?: 'pnl' | 'balance_sheet' | 'both';
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/financial-reports/export-all', {
      params,
      responseType: 'blob',
      timeout: 300000, // 5 min timeout for bulk export
    });
    return response.data;
  },

  fccsValidation: async (params: {
    entity_code: string;
    statement_type: string;
    year: number;
  }): Promise<any> => {
    const response = await apiClient.get('/reports/fccs-validation', { params });
    return response.data;
  },

  fccsValidationExport: async (params: {
    entity_id: number;
    entity_code: string;
    year: number;
  }): Promise<Blob> => {
    const response = await apiClient.get('/reports/fccs-validation/export', {
      params,
      responseType: 'blob',
      timeout: 300000, // 5 min timeout for validation export
    });
    return response.data;
  },

  validationFiles: async (): Promise<any[]> => {
    const response = await apiClient.get('/reports/validation-files');
    return response.data;
  },

  uploadValidationFile: async (fileType: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/reports/validation-files/upload?file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteValidationFile: async (fileId: number): Promise<any> => {
    const response = await apiClient.delete(`/reports/validation-files/${fileId}`);
    return response.data;
  },

  consolidationReviewSummary: async (params: {
    period_id: string;
  }): Promise<ConsolidationReviewSummary> => {
    const response = await apiClient.get<ConsolidationReviewSummary>('/reports/consolidation-review-summary', {
      params,
    });
    return response.data;
  },

  /** FCCS-style multi-column consolidation review (local → translated → proportion → IC → NCI → contribution). */
  consolidationReviewMatrix: async (params: {
    period_id: string;
    /** Limit columns to this entity's subtree (matches Consolidation company selector). */
    scope_entity_id?: string;
  }): Promise<ConsolidationReviewMatrix> => {
    const response = await apiClient.get<ConsolidationReviewMatrix>('/reports/consolidation-review-matrix', {
      params,
    });
    return response.data;
  },

  consolidationReviewMatrixExport: async (params: { period_id: string; scope_entity_id?: string; detailed?: boolean }): Promise<Blob> => {
    const response = await apiClient.get('/reports/consolidation-review-matrix/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// --- Periods API -------------------------------------------------------------

export const periodsApi = {
  list: async (params?: { status?: string }): Promise<Period[]> => {
    const response = await apiClient.get<any>('/periods', { params });
    return response.data?.items ?? response.data;
  },

  create: async (data: Partial<Period>): Promise<Period> => {
    const response = await apiClient.post<Period>('/periods', data);
    return response.data;
  },

  get: async (id: string): Promise<Period> => {
    const response = await apiClient.get<Period>(`/periods/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<Period>): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}`, data);
    return response.data;
  },

  close: async (id: string): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}/status`, { status: 'closed' });
    return response.data;
  },

  lock: async (id: string): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}/status`, { status: 'locked' });
    return response.data;
  },

  reopen: async (id: string): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}/status`, { status: 'open' });
    return response.data;
  },

  updateStatus: async (id: string, status: string): Promise<Period> => {
    const response = await apiClient.put<Period>(`/periods/${id}/status`, { status });
    return response.data;
  },
};

// --- Currencies API ----------------------------------------------------------

export const currenciesApi = {
  list: async (params?: { is_active?: boolean }): Promise<Currency[]> => {
    const response = await apiClient.get<Currency[]>('/currencies', { params });
    return response.data;
  },

  create: async (data: { code: string; name: string; symbol?: string }): Promise<Currency> => {
    const response = await apiClient.post<Currency>('/currencies', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; symbol?: string; is_active?: boolean }): Promise<Currency> => {
    const response = await apiClient.put<Currency>(`/currencies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/currencies/${id}`);
  },
};

// --- FX Rates API ------------------------------------------------------------

export const fxRatesApi = {
  list: async (params?: { period_id?: string }): Promise<FxRate[]> => {
    const response = await apiClient.get<any>('/fx-rates', { params });
    return response.data?.items ?? response.data;
  },

  create: async (data: Partial<FxRate>): Promise<FxRate> => {
    const response = await apiClient.post<FxRate>('/fx-rates', data);
    return response.data;
  },

  bulkCreate: async (data: Partial<FxRate>[]): Promise<FxRate[]> => {
    const response = await apiClient.post<FxRate[]>('/fx-rates/bulk', data);
    return response.data;
  },

  update: async (id: string, data: Partial<FxRate>): Promise<FxRate> => {
    const response = await apiClient.put<FxRate>(`/fx-rates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/fx-rates/${id}`);
  },
};

// --- Entity Code Mapping API -------------------------------------------------

export const entityCodeMappingApi = {
  list: async (params?: {
    source_system?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<EntityCodeMapping>> => {
    const response = await apiClient.get<PaginatedResponse<EntityCodeMapping>>(
      '/entity-code-mappings',
      { params }
    );
    return response.data;
  },

  create: async (data: {
    source_system: string;
    external_code: string;
    entity_id: number;
  }): Promise<EntityCodeMapping> => {
    const response = await apiClient.post<EntityCodeMapping>(
      '/entity-code-mappings',
      data
    );
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<{ source_system: string; external_code: string; entity_id: number }>
  ): Promise<EntityCodeMapping> => {
    const response = await apiClient.put<EntityCodeMapping>(
      `/entity-code-mappings/${id}`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/entity-code-mappings/${id}`);
  },

  validateCodes: async (codes: string[], sourceSystem = 'Excel'): Promise<ValidateCodesResponse> => {
    const response = await apiClient.post<ValidateCodesResponse>(
      '/entity-code-mappings/validate-codes',
      { codes, source_system: sourceSystem }
    );
    return response.data;
  },

  unmappedEntityIds: async (sourceSystem = 'Excel'): Promise<{ unmapped_entity_ids: number[] }> => {
    const response = await apiClient.get<{ unmapped_entity_ids: number[] }>(
      '/entity-code-mappings/unmapped-entity-ids',
      { params: { source_system: sourceSystem } }
    );
    return response.data;
  },

  unmappedIcpCodes: async (sourceSystem = 'Excel'): Promise<{ unmapped_icp_codes: string[] }> => {
    const response = await apiClient.get<{ unmapped_icp_codes: string[] }>(
      '/entity-code-mappings/unmapped-icp-codes',
      { params: { source_system: sourceSystem } }
    );
    return response.data;
  },

  bulkCreate: async (data: {
    source_system?: string;
    mappings: Array<{ external_code: string; entity_id: number }>;
  }): Promise<{ created: EntityCodeMapping[]; errors: string[]; skipped: number }> => {
    const response = await apiClient.post<{
      created: EntityCodeMapping[];
      errors: string[];
      skipped: number;
    }>('/entity-code-mappings/bulk', {
      source_system: data.source_system ?? 'Excel',
      mappings: data.mappings,
    });
    return response.data;
  },

  /** One-time CSV import: FCCS_Intercompany Entities + Intercompany Entities columns. */
  uploadCsv: async (
    file: File,
    sourceSystem = 'FCCS'
  ): Promise<{
    created: EntityCodeMapping[];
    updated: EntityCodeMapping[];
    skipped: number;
    errors: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      '/entity-code-mappings/upload-csv',
      formData,
      {
        params: { source_system: sourceSystem },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
};

// --- Intercompany API --------------------------------------------------------

export const intercompanyApi = {
  list: async (params?: {
    period_id?: string;
    from_date?: string;
    to_date?: string;
    entity_id?: string;
    match_status?: string;
    page?: number;
    page_size?: number;
    /** When true (default on backend), hide lines already paired / cleared. */
    work_queue_only?: boolean;
  }): Promise<PaginatedResponse<IntercompanyTransaction>> => {
    const response = await apiClient.get<PaginatedResponse<IntercompanyTransaction>>('/intercompany', { params });
    return response.data;
  },

  create: async (data: Partial<IntercompanyTransaction>): Promise<IntercompanyTransaction> => {
    const response = await apiClient.post<IntercompanyTransaction>('/intercompany', data);
    return response.data;
  },

  bulkCreate: async (data: Partial<IntercompanyTransaction>[]): Promise<IntercompanyTransaction[]> => {
    const response = await apiClient.post<IntercompanyTransaction[]>('/intercompany/bulk', data);
    return response.data;
  },

  update: async (id: string, data: Partial<IntercompanyTransaction>): Promise<IntercompanyTransaction> => {
    const response = await apiClient.put<IntercompanyTransaction>(`/intercompany/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/intercompany/${id}`);
  },

  summary: async (params: {
    period_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<IntercompanySummary> => {
    const response = await apiClient.get<IntercompanySummary>(`/intercompany/summary`, {
      params,
    });
    return response.data;
  },

  reconciliationReport: async (periodId: string): Promise<IntercompanyReconciliationReport> => {
    const response = await apiClient.get<IntercompanyReconciliationReport>(
      '/intercompany/reconciliation-report',
      { params: { period_id: periodId } }
    );
    return response.data;
  },

  eliminationCompleteness: async (periodId: string): Promise<{
    period_id: number;
    is_complete: boolean;
    message: string;
    matched_count: number;
    unmatched_count: number;
    mirror_gap_count?: number;
    total_ic_exposure: string;
  }> => {
    const response = await apiClient.get('/intercompany/elimination-completeness', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  createDisputesFromUnmatched: async (periodId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/intercompany/create-disputes-from-unmatched',
      null,
      { params: { period_id: periodId } }
    );
    return response.data;
  },

  workflowReadiness: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    period_id: number;
    source_system: string;
    metrics: Record<string, unknown>;
    steps: Array<{ id: string; title: string; ok: boolean; detail: string }>;
  }> => {
    const response = await apiClient.get('/intercompany/workflow-readiness', {
      params: { period_id: periodId, source_system: sourceSystem },
    });
    return response.data;
  },

  syncFromTrialBalance: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    removed: number;
    created: number;
    skipped_no_icp_mapping?: number;
    skipped_partner_config?: number;
    skipped_zero_net?: number;
    message: string;
  }> => {
    const response = await apiClient.post(
      '/intercompany/sync-from-trial-balance',
      {},
      { params: { period_id: periodId, source_system: sourceSystem } }
    );
    return response.data;
  },

  importFromTrialBalance: async (
    periodId: string,
    sourceSystem = 'Excel'
  ): Promise<{
    removed: number;
    created: number;
    message: string;
    skipped_no_icp_mapping?: number;
    skipped_partner_config?: number;
  }> => {
    const response = await apiClient.post(
      '/intercompany/import-from-trial-balance',
      {},
      { params: { period_id: periodId, source_system: sourceSystem } }
    );
    return response.data;
  },

  testElimination: async (
    periodId: string,
    sourceSystem = 'Excel',
    transactionTypeDefault = 'trade'
  ): Promise<IntercompanyEliminationTestResult> => {
    const response = await apiClient.post<IntercompanyEliminationTestResult>(
      '/intercompany/test-elimination',
      {},
      {
        params: {
          period_id: periodId,
          source_system: sourceSystem,
          transaction_type_default: transactionTypeDefault,
        },
      }
    );
    return response.data;
  },

  downloadEliminationWorksheet: async (periodId: string): Promise<void> => {
    // POST avoids 405 from some proxies that only allow POST under /api.
    const response = await apiClient.post('/intercompany/elimination-worksheet', {}, {
      params: { period_id: periodId },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ic_elimination_worksheet_period_${periodId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- IC Disputes API ---------------------------------------------------------

export const icDisputesApi = {
  list: async (params?: {
    period_id?: number;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<IcDispute>> => {
    const response = await apiClient.get<PaginatedResponse<IcDispute>>('/ic-disputes', { params });
    return response.data;
  },

  create: async (data: {
    period_id: number;
    entity_from_id: number;
    entity_to_id: number;
    amount_from: number;
    amount_to: number;
    currency: string;
    transaction_type: string;
    description?: string;
    transaction_from_id?: number;
    transaction_to_id?: number;
    resolution_deadline?: string;
  }): Promise<IcDispute> => {
    const response = await apiClient.post<IcDispute>('/ic-disputes', data);
    return response.data;
  },

  update: async (
    id: number,
    data: Partial<{ status: string; proposed_adjustment: string; resolution_notes: string; escalated_to_user_id: number }>
  ): Promise<IcDispute> => {
    const response = await apiClient.put<IcDispute>(`/ic-disputes/${id}`, data);
    return response.data;
  },
};

// --- Users API ---------------------------------------------------------------

export const usersApi = {
  list: async (): Promise<User[]> => {
    const response = await apiClient.get<any>('/users');
    return response.data?.items ?? response.data;
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await apiClient.post<User>('/users', data);
    return response.data;
  },

  get: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: UserUpdate): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  changePassword: async (id: string, data: ChangePasswordRequest): Promise<void> => {
    await apiClient.put(`/users/${id}/password`, data);
  },
};

// --- Skills API --------------------------------------------------------------

export const skillsApi = {
  list: async (): Promise<SkillExecution[]> => {
    const response = await apiClient.get<SkillExecution[]>('/skills');
    return response.data;
  },

  execute: async (skillName: string, params?: Record<string, unknown>): Promise<SkillExecution> => {
    const response = await apiClient.post<SkillExecution>(`/skills/${skillName}/execute`, params);
    return response.data;
  },
};

// --- Audit API ---------------------------------------------------------------

export const auditApi = {
  list: async (params: {
    page?: number;
    action?: string;
    resource_type?: string;
    user_id?: number;
    from_date?: string;
    to_date?: string;
  }): Promise<PaginatedResponse<AuditLog>> => {
    const response = await apiClient.get<PaginatedResponse<AuditLog>>('/audit', { params });
    return response.data;
  },

  get: async (id: number): Promise<AuditLog> => {
    const response = await apiClient.get<AuditLog>(`/audit/${id}`);
    return response.data;
  },

  summary: async (): Promise<AuditSummary> => {
    const response = await apiClient.get<AuditSummary>('/audit/summary');
    return response.data;
  },
};

// --- Tax API -----------------------------------------------------------------

export const taxApi = {
  compute: async (data: {
    period_id: number;
    entity_ids?: number[];
    parameters?: Record<string, any>;
  }): Promise<TaxComputeResponse> => {
    const response = await apiClient.post<TaxComputeResponse>('/tax/compute', data);
    return response.data;
  },

  results: async (periodId: number): Promise<TaxComputeResponse> => {
    const response = await apiClient.get<TaxComputeResponse>('/tax/results', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  entityDetail: async (entityId: number, periodId: number): Promise<EntityTaxDetail> => {
    const response = await apiClient.get<EntityTaxDetail>(`/tax/entities/${entityId}/detail`, {
      params: { period_id: periodId },
    });
    return response.data;
  },

  adjustments: async (entityId: number, data: any): Promise<EntityTaxDetail> => {
    const response = await apiClient.put<EntityTaxDetail>(`/tax/entities/${entityId}/adjustments`, data);
    return response.data;
  },

  qfzpStatus: async (): Promise<QfzpEntityStatus[]> => {
    const response = await apiClient.get<QfzpEntityStatus[]>('/tax/qfzp-status');
    return response.data;
  },

  sbrEligible: async (): Promise<EntityTaxDetail[]> => {
    const response = await apiClient.get<EntityTaxDetail[]>('/tax/sbr-eligible');
    return response.data;
  },
};

// --- Transfer Pricing API ----------------------------------------------------

export const transferPricingApi = {
  analyze: async (data: { period_id: number }): Promise<TPTransaction[]> => {
    const response = await apiClient.post<TPTransaction[]>('/transfer-pricing/analyze', data);
    return response.data;
  },

  results: async (periodId: number): Promise<TPTransaction[]> => {
    const response = await apiClient.get<TPTransaction[]>('/transfer-pricing/results', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  transaction: async (id: number): Promise<TPTransaction> => {
    const response = await apiClient.get<TPTransaction>(`/transfer-pricing/transactions/${id}`);
    return response.data;
  },

  documentation: async (data: { period_id: number }): Promise<Blob> => {
    const response = await apiClient.post('/transfer-pricing/documentation', data, {
      responseType: 'blob',
    });
    return response.data;
  },

  summary: async (periodId: number): Promise<TPSummary> => {
    const response = await apiClient.get<TPSummary>('/transfer-pricing/summary', {
      params: { period_id: periodId },
    });
    return response.data;
  },
};

// --- Anomaly Detection API ---------------------------------------------------

export const anomalyApi = {
  detect: async (data: {
    period_id: number;
    parameters?: Record<string, any>;
  }): Promise<Anomaly[]> => {
    const response = await apiClient.post<Anomaly[]>('/anomalies/detect', data);
    return response.data;
  },

  results: async (
    periodId: number,
    params?: { severity?: string; category?: string },
  ): Promise<Anomaly[]> => {
    const response = await apiClient.get<Anomaly[]>('/anomalies/results', {
      params: { period_id: periodId, ...params },
    });
    return response.data;
  },

  summary: async (periodId: number): Promise<AnomalySummary> => {
    const response = await apiClient.get<AnomalySummary>('/anomalies/summary', {
      params: { period_id: periodId },
    });
    return response.data;
  },

  get: async (id: number): Promise<Anomaly> => {
    const response = await apiClient.get<Anomaly>(`/anomalies/${id}`);
    return response.data;
  },

  resolve: async (
    id: number,
    data: { resolution_status: string; resolution_notes: string },
  ): Promise<Anomaly> => {
    const response = await apiClient.put<Anomaly>(`/anomalies/${id}/resolve`, data);
    return response.data;
  },

  benford: async (periodId: number): Promise<BenfordAnalysis> => {
    const response = await apiClient.get<BenfordAnalysis>('/anomalies/benford', {
      params: { period_id: periodId },
    });
    return response.data;
  },
};

// --- Exports API -------------------------------------------------------------

export const exportsApi = {
  balanceSheet: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/balance-sheet', data, { responseType: 'blob' });
    return response.data;
  },

  incomeStatement: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/income-statement', data, { responseType: 'blob' });
    return response.data;
  },

  cashFlow: async (data: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/exports/cash-flow', data, { responseType: 'blob' });
    return response.data;
  },

  trialBalance: async (data: {
    report_type: string;
    period_id: number;
    entity_id: number;
    format?: string;
    company_name?: string;
    export_original?: boolean;
    export_translated?: boolean;
    include_cta?: boolean;
    to_currency?: string;
  }): Promise<Blob> => {
    const response = await apiClient.post('/exports/trial-balance', data, { responseType: 'blob' });
    return response.data;
  },

  consolidationPackage: async (data: any): Promise<Blob> => {
    const response = await apiClient.post('/exports/consolidation-package', data, { responseType: 'blob' });
    return response.data;
  },
};

// --- ERP API -----------------------------------------------------------------

export const erpApi = {
  connectors: async (): Promise<ERPConnector[]> => {
    const response = await apiClient.get<ERPConnector[]>('/erp/connectors');
    return response.data;
  },

  testConnection: async (data: any): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>('/erp/test-connection', data);
    return response.data;
  },

  importTrialBalance: async (data: any): Promise<{ count: number; message: string }> => {
    const response = await apiClient.post<{ count: number; message: string }>('/erp/import/trial-balance', data);
    return response.data;
  },

  importChartOfAccounts: async (data: any): Promise<{ count: number; message: string }> => {
    const response = await apiClient.post<{ count: number; message: string }>('/erp/import/chart-of-accounts', data);
    return response.data;
  },
};

// --- Notifications API -------------------------------------------------------

export const notificationsApi = {
  list: async (params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<Notification>> => {
    const response = await apiClient.get<PaginatedResponse<Notification>>('/notifications', { params });
    return response.data;
  },

  unreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get<{ count: number }>('/notifications/unread-count');
    return response.data;
  },

  markRead: async (id: string): Promise<void> => {
    await apiClient.put(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await apiClient.put('/notifications/read-all');
  },
};

// --- Approvals API -----------------------------------------------------------

export const approvalsApi = {
  list: async (params?: { status?: string; workflow_type?: string }): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals', { params });
    return response.data;
  },

  pending: async (): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals/pending');
    return response.data;
  },

  myRequests: async (): Promise<ApprovalWorkflow[]> => {
    const response = await apiClient.get<ApprovalWorkflow[]>('/approvals/my-requests');
    return response.data;
  },

  approve: async (id: string, comments?: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>(`/approvals/${id}/approve`, { comments });
    return response.data;
  },

  reject: async (id: string, comments?: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>(`/approvals/${id}/reject`, { comments });
    return response.data;
  },

  create: async (data: Partial<ApprovalWorkflow>): Promise<ApprovalWorkflow> => {
    const response = await apiClient.post<ApprovalWorkflow>('/approvals', data);
    return response.data;
  },
};

// --- Natural Language Query API -----------------------------------------------

export const nlQueryApi = {
  submit: async (query: string): Promise<NLQueryResponse> => {
    const response = await apiClient.post<NLQueryResponse>('/nl-query', { query });
    return response.data;
  },

  suggestions: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/nl-query/suggestions');
    return response.data;
  },
};

// --- XBRL API ----------------------------------------------------------------

export const xbrlApi = {
  generate: async (data: { consolidation_run_id: string; taxonomy?: string }): Promise<Blob> => {
    const response = await apiClient.post('/xbrl/generate', data, { responseType: 'blob' });
    return response.data;
  },

  taxonomy: async (): Promise<{ id: string; name: string; version: string }[]> => {
    const response = await apiClient.get<{ id: string; name: string; version: string }[]>('/xbrl/taxonomies');
    return response.data;
  },

  mappings: async (): Promise<XBRLMapping[]> => {
    const response = await apiClient.get<XBRLMapping[]>('/xbrl/mappings');
    return response.data;
  },

  updateMapping: async (accountCode: string, data: Partial<XBRLMapping>): Promise<XBRLMapping> => {
    const response = await apiClient.put<XBRLMapping>(`/xbrl/mappings/${accountCode}`, data);
    return response.data;
  },

  validate: async (data: { consolidation_run_id: string }): Promise<XBRLValidationResult> => {
    const response = await apiClient.post<XBRLValidationResult>('/xbrl/validate', data);
    return response.data;
  },

  preview: async (data: { consolidation_run_id: string }): Promise<{ content: string; format: string }> => {
    const response = await apiClient.post<{ content: string; format: string }>('/xbrl/preview', data);
    return response.data;
  },
};

// --- Background Tasks API ----------------------------------------------------

export const tasksApi = {
  list: async (): Promise<BackgroundTask[]> => {
    const response = await apiClient.get<BackgroundTask[]>('/tasks');
    return response.data;
  },

  get: async (id: string): Promise<BackgroundTask> => {
    const response = await apiClient.get<BackgroundTask>(`/tasks/${id}`);
    return response.data;
  },

  cancel: async (id: string): Promise<void> => {
    await apiClient.post(`/tasks/${id}/cancel`);
  },
};

// --- SSO API -----------------------------------------------------------------

export const ssoApi = {
  providers: async (): Promise<SSOProvider[]> => {
    const response = await apiClient.get<SSOProvider[]>('/auth/sso/providers');
    return response.data;
  },

  configure: async (providerId: string, data: Record<string, any>): Promise<SSOProvider> => {
    const response = await apiClient.put<SSOProvider>(`/auth/sso/providers/${providerId}`, data);
    return response.data;
  },
};

// --- MFA API -----------------------------------------------------------------

export const mfaApi = {
  setup: async (): Promise<MFASetup> => {
    const response = await apiClient.post<MFASetup>('/auth/mfa/setup');
    return response.data;
  },

  verify: async (code: string): Promise<{ verified: boolean }> => {
    const response = await apiClient.post<{ verified: boolean }>('/auth/mfa/verify', { code });
    return response.data;
  },

  disable: async (code: string): Promise<void> => {
    await apiClient.post('/auth/mfa/disable', { code });
  },

  status: async (): Promise<{ enabled: boolean }> => {
    const response = await apiClient.get<{ enabled: boolean }>('/auth/mfa/status');
    return response.data;
  },
};

// --- EmaraTax API ------------------------------------------------------------

export const emaraTaxApi = {
  prepare: async (data: { period_id: string; return_type?: string }): Promise<TaxReturn> => {
    const response = await apiClient.post<TaxReturn>('/emara-tax/prepare', data);
    return response.data;
  },

  validate: async (id: string): Promise<{ is_valid: boolean; errors: string[] }> => {
    const response = await apiClient.post<{ is_valid: boolean; errors: string[] }>(`/emara-tax/${id}/validate`);
    return response.data;
  },

  submit: async (id: string): Promise<TaxReturn> => {
    const response = await apiClient.post<TaxReturn>(`/emara-tax/${id}/submit`);
    return response.data;
  },

  list: async (params?: { period_id?: string }): Promise<TaxReturn[]> => {
    const response = await apiClient.get<TaxReturn[]>('/emara-tax', { params });
    return response.data;
  },
};

// --- ESG API -----------------------------------------------------------------

export const esgApi = {
  metrics: async (params?: { entity_id?: string; category?: string; period_id?: string }): Promise<ESGMetric[]> => {
    const response = await apiClient.get<ESGMetric[]>('/esg/metrics', { params });
    return response.data;
  },

  createMetric: async (data: Partial<ESGMetric>): Promise<ESGMetric> => {
    const response = await apiClient.post<ESGMetric>('/esg/metrics', data);
    return response.data;
  },

  report: async (params?: { period_id?: string; framework?: string }): Promise<Blob> => {
    const response = await apiClient.get('/esg/report', { params, responseType: 'blob' });
    return response.data;
  },

  dashboard: async (params?: { period_id?: string }): Promise<ESGDashboardData> => {
    const response = await apiClient.get<ESGDashboardData>('/esg/dashboard', { params });
    return response.data;
  },

  targets: async (): Promise<ESGTarget[]> => {
    const response = await apiClient.get<ESGTarget[]>('/esg/targets');
    return response.data;
  },

  createTarget: async (data: Partial<ESGTarget>): Promise<ESGTarget> => {
    const response = await apiClient.post<ESGTarget>('/esg/targets', data);
    return response.data;
  },
};

// --- Forecasting API ---------------------------------------------------------

export const forecastApi = {
  generate: async (data: {
    entity_ids?: string[];
    account_codes?: string[];
    periods_ahead?: number;
    method?: string;
  }): Promise<ForecastResult[]> => {
    const response = await apiClient.post<ForecastResult[]>('/forecasts/generate', data);
    return response.data;
  },

  list: async (params?: { entity_id?: string }): Promise<ForecastResult[]> => {
    const response = await apiClient.get<ForecastResult[]>('/forecasts', { params });
    return response.data;
  },

  get: async (id: string): Promise<ForecastResult> => {
    const response = await apiClient.get<ForecastResult>(`/forecasts/${id}`);
    return response.data;
  },

  compareActuals: async (forecastId: string): Promise<{
    forecast: ForecastResult;
    actuals: { period: string; value: number }[];
    accuracy_pct: number;
  }> => {
    const response = await apiClient.get(`/forecasts/${forecastId}/compare-actuals`);
    return response.data;
  },

  export: async (params: { entity_id?: string; format?: string }): Promise<Blob> => {
    const response = await apiClient.get('/forecasts/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// --- Multi-GAAP API ----------------------------------------------------------

export const multiGaapApi = {
  convert: async (data: {
    period_id: string;
    source_gaap: string;
    target_gaap: string;
  }): Promise<GaapConversion> => {
    const response = await apiClient.post<GaapConversion>('/multi-gaap/convert', data);
    return response.data;
  },

  standards: async (): Promise<{ code: string; name: string; description: string }[]> => {
    const response = await apiClient.get<{ code: string; name: string; description: string }[]>('/multi-gaap/standards');
    return response.data;
  },

  reconciliation: async (params: {
    period_id: string;
    source_gaap: string;
    target_gaap: string;
  }): Promise<GaapConversion> => {
    const response = await apiClient.get<GaapConversion>('/multi-gaap/reconciliation', { params });
    return response.data;
  },
};

// --- Marketplace API ---------------------------------------------------------

export const marketplaceApi = {
  list: async (params?: { category?: string; search?: string }): Promise<MarketplaceSkill[]> => {
    const response = await apiClient.get<MarketplaceSkill[]>('/marketplace/skills', { params });
    return response.data;
  },

  get: async (skillId: string): Promise<MarketplaceSkill> => {
    const response = await apiClient.get<MarketplaceSkill>(`/marketplace/skills/${skillId}`);
    return response.data;
  },

  install: async (skillId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/marketplace/skills/${skillId}/install`);
    return response.data;
  },

  uninstall: async (skillId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/marketplace/skills/${skillId}/uninstall`);
    return response.data;
  },

  installed: async (): Promise<MarketplaceSkill[]> => {
    const response = await apiClient.get<MarketplaceSkill[]>('/marketplace/installed');
    return response.data;
  },
};

// --- Blockchain Audit API ----------------------------------------------------

export const blockchainAuditApi = {
  chain: async (params?: {
    page?: number;
    page_size?: number;
    event_type?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<PaginatedResponse<AuditBlock>> => {
    const response = await apiClient.get<PaginatedResponse<AuditBlock>>('/blockchain-audit/chain', { params });
    return response.data;
  },

  verify: async (): Promise<{ is_valid: boolean; blocks_verified: number; errors: string[] }> => {
    const response = await apiClient.post<{ is_valid: boolean; blocks_verified: number; errors: string[] }>('/blockchain-audit/verify');
    return response.data;
  },

  stats: async (): Promise<AuditChainStats> => {
    const response = await apiClient.get<AuditChainStats>('/blockchain-audit/stats');
    return response.data;
  },

  block: async (id: string): Promise<AuditBlock> => {
    const response = await apiClient.get<AuditBlock>(`/blockchain-audit/blocks/${id}`);
    return response.data;
  },

  export: async (params: { from_date?: string; to_date?: string }): Promise<Blob> => {
    const response = await apiClient.get('/blockchain-audit/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// --- Scenario Modeling API ---------------------------------------------------

export const scenarioApi = {
  run: async (data: {
    scenario_type: string;
    name?: string;
    parameters: Record<string, any>;
  }): Promise<ScenarioResult> => {
    const response = await apiClient.post<ScenarioResult>('/scenarios/run', data);
    return response.data;
  },

  list: async (): Promise<ScenarioResult[]> => {
    const response = await apiClient.get<ScenarioResult[]>('/scenarios');
    return response.data;
  },

  get: async (id: string): Promise<ScenarioResult> => {
    const response = await apiClient.get<ScenarioResult>(`/scenarios/${id}`);
    return response.data;
  },

  compare: async (ids: string[]): Promise<ScenarioResult[]> => {
    const response = await apiClient.post<ScenarioResult[]>('/scenarios/compare', { scenario_ids: ids });
    return response.data;
  },
};

// --- Phase 9: Data Quality API -----------------------------------------------

export const dataQualityApi = {
  get: async (entityId: number, periodId: number): Promise<import('@/types').DataQualityReport> => {
    const response = await apiClient.get(`/data-quality/entity/${entityId}/period/${periodId}`);
    return response.data;
  },

  run: async (periodId: number): Promise<import('@/types').DataQualityReport[]> => {
    const response = await apiClient.post(`/data-quality/period/${periodId}`);
    return response.data;
  },

  summary: async (periodId: number): Promise<import('@/types').DataQualityReport[]> => {
    const response = await apiClient.get(`/data-quality/period/${periodId}/summary`);
    return response.data;
  },
};

// --- Phase 9: Predictive Close API -------------------------------------------

export const predictiveCloseApi = {
  get: async (periodId: number): Promise<import('@/types').CloseTimeline> => {
    const response = await apiClient.get(`/predictive-close/${periodId}`);
    return response.data;
  },

  readiness: async (periodId: number): Promise<import('@/types').EntityReadiness[]> => {
    const response = await apiClient.get(`/predictive-close/${periodId}/readiness`);
    return response.data;
  },

  bottlenecks: async (periodId: number): Promise<import('@/types').Bottleneck[]> => {
    const response = await apiClient.get(`/predictive-close/${periodId}/bottlenecks`);
    return response.data;
  },
};

// --- Phase 10: KPI API -------------------------------------------------------

export const kpiApi = {
  dashboard: async (periodId: number): Promise<import('@/types').KPIDashboard> => {
    const response = await apiClient.get(`/kpi/dashboard/${periodId}`);
    return response.data;
  },

  trend: async (kpiId: string): Promise<{ periods: string[]; values: number[] }> => {
    const response = await apiClient.get(`/kpi/${kpiId}/trend`);
    return response.data;
  },
};

// --- Phase 10: Close Status API ----------------------------------------------

export const closeStatusApi = {
  get: async (periodId: number): Promise<import('@/types').PeriodCloseStatus> => {
    const response = await apiClient.get(`/close-status/period/${periodId}`);
    return response.data;
  },

  updateStage: async (periodId: number, entityId: number, stage: string, data: any): Promise<void> => {
    await apiClient.put(`/close-status/period/${periodId}/entity/${entityId}/stage/${stage}`, data);
  },
};

// --- Phase 10: Rules API -----------------------------------------------------

export const rulesApi = {
  list: async (): Promise<import('@/types').ConsolidationRule[]> => {
    const response = await apiClient.get('/rules');
    return response.data;
  },

  create: async (data: Partial<import('@/types').ConsolidationRule>): Promise<import('@/types').ConsolidationRule> => {
    const response = await apiClient.post('/rules', data);
    return response.data;
  },

  update: async (id: number, data: Partial<import('@/types').ConsolidationRule>): Promise<import('@/types').ConsolidationRule> => {
    const response = await apiClient.put(`/rules/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/rules/${id}`);
  },

  evaluate: async (periodId: number): Promise<{ matched: number; actions_taken: number; journal_entries: number }> => {
    const response = await apiClient.post(`/rules/evaluate/${periodId}`);
    return response.data;
  },
};

// --- Phase 10: Auto Mapper API -----------------------------------------------

export const autoMapperApi = {
  suggest: async (entityId: number): Promise<import('@/types').MappingSuggestion[]> => {
    const response = await apiClient.post(`/auto-mapper/suggest/${entityId}`);
    return response.data;
  },

  confirm: async (data: { entity_account_id: number; group_account_id: number }): Promise<void> => {
    await apiClient.post('/auto-mapper/confirm', data);
  },
};

// --- Phase 10: Budget API ----------------------------------------------------

export const budgetApi = {
  list: async (periodId: number): Promise<import('@/types').BudgetEntry[]> => {
    const response = await apiClient.get(`/budget/period/${periodId}`);
    return response.data;
  },

  create: async (data: Partial<import('@/types').BudgetEntry>): Promise<import('@/types').BudgetEntry> => {
    const response = await apiClient.post('/budget', data);
    return response.data;
  },

  update: async (id: number, data: Partial<import('@/types').BudgetEntry>): Promise<import('@/types').BudgetEntry> => {
    const response = await apiClient.put(`/budget/${id}`, data);
    return response.data;
  },

  variance: async (periodId: number): Promise<import('@/types').BudgetVariance[]> => {
    const response = await apiClient.get(`/budget/variance/${periodId}`);
    return response.data;
  },

  approve: async (id: number): Promise<void> => {
    await apiClient.post(`/budget/${id}/approve`);
  },
};

// --- Phase 10: Dimensions API ------------------------------------------------

export const dimensionsApi = {
  types: async (): Promise<{ code: string; name: string; description: string }[]> => {
    const response = await apiClient.get('/dimensions/types');
    return response.data;
  },

  values: async (type: string): Promise<{ id: number; code: string; name: string }[]> => {
    const response = await apiClient.get(`/dimensions/${type}/values`);
    return response.data;
  },

  createValue: async (type: string, data: any): Promise<{ id: number; code: string; name: string }> => {
    const response = await apiClient.post(`/dimensions/${type}/values`, data);
    return response.data;
  },
};

// --- Phase 10: Permissions API -----------------------------------------------

export const permissionsApi = {
  getUserPermissions: async (userId: number): Promise<any> => {
    const response = await apiClient.get(`/permissions/user/${userId}`);
    return response.data;
  },

  grant: async (data: any): Promise<void> => {
    await apiClient.post('/permissions', data);
  },
};

// --- Phase 11: i18n API ------------------------------------------------------

export const i18nApi = {
  translations: async (locale: string): Promise<Record<string, string>> => {
    const response = await apiClient.get(`/i18n/translations/${locale}`);
    return response.data;
  },

  locales: async (): Promise<{ code: string; name: string; direction: string }[]> => {
    const response = await apiClient.get('/i18n/locales');
    return response.data;
  },

  setPreference: async (locale: string): Promise<void> => {
    await apiClient.put('/i18n/preference', { locale });
  },
};

// --- Phase 11: Search API ----------------------------------------------------

export const searchApi = {
  search: async (query: string, type?: string): Promise<import('@/types').SearchResult[]> => {
    const response = await apiClient.get('/search', { params: { q: query, type } });
    return response.data;
  },
};

// --- Phase 11: CDC API -------------------------------------------------------

export const cdcApi = {
  changes: async (params?: any): Promise<import('@/types').PaginatedResponse<import('@/types').CDCRecord>> => {
    const response = await apiClient.get('/cdc/changes', { params });
    return response.data;
  },

  stats: async (): Promise<{ total_changes: number; pending_sync: number; last_synced_at: string | null }> => {
    const response = await apiClient.get('/cdc/stats');
    return response.data;
  },
};

// --- Phase 11: Recurring Adjustments API -------------------------------------

export const recurringAdjustmentsApi = {
  templates: async (): Promise<import('@/types').RecurringTemplate[]> => {
    const response = await apiClient.get('/recurring-adjustments/templates');
    return response.data;
  },

  createTemplate: async (data: Partial<import('@/types').RecurringTemplate>): Promise<import('@/types').RecurringTemplate> => {
    const response = await apiClient.post('/recurring-adjustments/templates', data);
    return response.data;
  },

  suggestions: async (periodId: number): Promise<import('@/types').RecurringTemplate[]> => {
    const response = await apiClient.get(`/recurring-adjustments/suggestions/${periodId}`);
    return response.data;
  },
};

// --- Phase 12: Webhooks API --------------------------------------------------

export const webhookApi = {
  list: async (): Promise<import('@/types').WebhookSubscription[]> => {
    const response = await apiClient.get('/webhooks');
    return response.data;
  },

  create: async (data: any): Promise<import('@/types').WebhookSubscription> => {
    const response = await apiClient.post('/webhooks', data);
    return response.data;
  },

  update: async (id: number, data: any): Promise<import('@/types').WebhookSubscription> => {
    const response = await apiClient.put(`/webhooks/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/webhooks/${id}`);
  },

  test: async (id: number): Promise<{ success: boolean; status_code: number; duration_ms: number }> => {
    const response = await apiClient.post(`/webhooks/${id}/test`);
    return response.data;
  },

  deliveries: async (id: number): Promise<import('@/types').WebhookDelivery[]> => {
    const response = await apiClient.get(`/webhooks/${id}/deliveries`);
    return response.data;
  },
};

// --- Phase 12: API Keys API --------------------------------------------------

export const apiKeyApi = {
  list: async (): Promise<import('@/types').APIKeyInfo[]> => {
    const response = await apiClient.get('/api-keys');
    return response.data;
  },

  create: async (data: any): Promise<{ key: string; info: import('@/types').APIKeyInfo }> => {
    const response = await apiClient.post('/api-keys', data);
    return response.data;
  },

  revoke: async (id: number): Promise<void> => {
    await apiClient.delete(`/api-keys/${id}`);
  },

  rotate: async (id: number): Promise<{ key: string; info: import('@/types').APIKeyInfo }> => {
    const response = await apiClient.post(`/api-keys/${id}/rotate`);
    return response.data;
  },
};

// --- Phase 12: Security API --------------------------------------------------

export const securityApi = {
  audit: async (): Promise<import('@/types').SecurityAuditReport> => {
    const response = await apiClient.get('/security/audit');
    return response.data;
  },

  compliance: async (): Promise<any> => {
    const response = await apiClient.get('/security/compliance');
    return response.data;
  },

  recommendations: async (): Promise<string[]> => {
    const response = await apiClient.get('/security/recommendations');
    return response.data;
  },
};

// --- Phase 12: Archives API --------------------------------------------------

export const archiveApi = {
  list: async (): Promise<import('@/types').PeriodArchive[]> => {
    const response = await apiClient.get('/archives');
    return response.data;
  },

  archive: async (periodId: number): Promise<import('@/types').PeriodArchive> => {
    const response = await apiClient.post(`/archives/period/${periodId}`);
    return response.data;
  },

  restore: async (periodId: number): Promise<void> => {
    await apiClient.post(`/archives/${periodId}/restore`);
  },

  storage: async (): Promise<import('@/types').StorageUsage> => {
    const response = await apiClient.get('/archives/storage');
    return response.data;
  },

  retentionPolicy: async (): Promise<{ min_retention_years: number; auto_archive_months: number }> => {
    const response = await apiClient.get('/archives/retention-policy');
    return response.data;
  },
};

// --- Phase 13: Policy Conflicts API ------------------------------------------

export const policyApi = {
  conflicts: async (): Promise<import('@/types').PolicyConflict[]> => {
    const response = await apiClient.get('/policies/conflicts');
    return response.data;
  },

  conflictSummary: async (): Promise<any> => {
    const response = await apiClient.get('/policies/conflicts/summary');
    return response.data;
  },

  groupPolicies: async (): Promise<any> => {
    const response = await apiClient.get('/policies/group');
    return response.data;
  },

  updatePolicy: async (key: string, data: any): Promise<void> => {
    await apiClient.put(`/policies/group/${key}`, data);
  },
};

// --- Phase 13: MPM API -------------------------------------------------------

export const mpmApi = {
  dashboard: async (periodId: number): Promise<import('@/types').MPMResult[]> => {
    const response = await apiClient.get(`/mpm/dashboard/${periodId}`);
    return response.data;
  },

  reconciliation: async (mpmId: string, periodId: number): Promise<any> => {
    const response = await apiClient.get(`/mpm/${mpmId}/reconciliation/${periodId}`);
    return response.data;
  },

  definitions: async (): Promise<any> => {
    const response = await apiClient.get('/mpm/definitions');
    return response.data;
  },
};

// --- Phase 13: Audit Package API ---------------------------------------------

export const auditPackageApi = {
  download: async (runId: number): Promise<Blob> => {
    const response = await apiClient.get(`/exports/audit-package/${runId}`, { responseType: 'blob' });
    return response.data;
  },

  contents: async (runId: number): Promise<any> => {
    const response = await apiClient.get(`/exports/audit-package/${runId}/contents`);
    return response.data;
  },
};

// --- Phase 13: IFRS Rule Library API -----------------------------------------

export const ruleLibraryApi = {
  getAll: async (): Promise<any[]> => {
    const response = await apiClient.get('/rules/library');
    return response.data;
  },

  seed: async (): Promise<void> => {
    await apiClient.post('/rules/library/seed');
  },

  categories: async (): Promise<string[]> => {
    const response = await apiClient.get('/rules/library/categories');
    return response.data;
  },
};

// --- Phase 14: Onboarding API ------------------------------------------------

export const onboardingApi = {
  progress: async (): Promise<import('@/types').OnboardingProgress> => {
    const response = await apiClient.get('/onboarding/progress');
    return response.data;
  },

  completeStep: async (step: string): Promise<void> => {
    await apiClient.post(`/onboarding/steps/${step}/complete`);
  },

  skipStep: async (step: string): Promise<void> => {
    await apiClient.post(`/onboarding/steps/${step}/skip`);
  },

  guidance: async (step: string): Promise<{ title: string; content: string; tips: string[] }> => {
    const response = await apiClient.get(`/onboarding/steps/${step}/guidance`);
    return response.data;
  },

  seedSampleData: async (): Promise<{ message: string }> => {
    const response = await apiClient.post('/onboarding/seed-sample-data');
    return response.data;
  },
};

// --- Phase 14: Import Wizard API ---------------------------------------------

export const importApi = {
  start: async (file: File, importType: string, data?: Record<string, string>): Promise<import('@/types').ImportSession> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importType);
    if (data) Object.entries(data).forEach(([k, v]) => formData.append(k, v));
    const response = await apiClient.post('/import/start', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getSession: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.get(`/import/${id}`);
    return response.data;
  },

  updateMapping: async (id: number, mappings: Record<string, string>): Promise<void> => {
    await apiClient.put(`/import/${id}/mapping`, { mappings });
  },

  validate: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.post(`/import/${id}/validate`);
    return response.data;
  },

  preview: async (id: number): Promise<{ rows: any[]; summary: { create: number; update: number; skip: number } }> => {
    const response = await apiClient.get(`/import/${id}/preview`);
    return response.data;
  },

  execute: async (id: number): Promise<import('@/types').ImportSession> => {
    const response = await apiClient.post(`/import/${id}/execute`);
    return response.data;
  },

  sessions: async (): Promise<import('@/types').ImportSession[]> => {
    const response = await apiClient.get('/import/sessions');
    return response.data;
  },
};

// --- Phase 14: Dashboard Preferences API -------------------------------------

export const dashboardPrefApi = {
  getLayout: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/dashboard/layout/${id}`);
    return response.data;
  },

  saveLayout: async (id: string, layout: any): Promise<void> => {
    await apiClient.put(`/dashboard/layout/${id}`, { layout });
  },

  getTheme: async (): Promise<{ theme: string }> => {
    const response = await apiClient.get('/dashboard/theme');
    return response.data;
  },

  setTheme: async (theme: string): Promise<void> => {
    await apiClient.put('/dashboard/theme', { theme });
  },
};

// --- Phase 14: Performance API -----------------------------------------------

export const performanceApi = {
  apiMetrics: async (): Promise<any> => {
    const response = await apiClient.get('/performance/api-metrics');
    return response.data;
  },

  consolidationMetrics: async (): Promise<any> => {
    const response = await apiClient.get('/performance/consolidation-metrics');
    return response.data;
  },

  systemHealth: async (): Promise<import('@/types').SystemHealth> => {
    const response = await apiClient.get('/performance/system-health');
    return response.data;
  },
};

// --- AI Config API -----------------------------------------------------------

export const aiConfigApi = {
  get: async (): Promise<{
    provider?: string;
    base_url?: string;
    api_key_masked?: string;
    model?: string;
    temperature?: number;
    max_tokens?: number;
    is_configured?: boolean;
  }> => {
    const response = await apiClient.get('/ai-config');
    return response.data;
  },

  save: async (config: {
    provider: string;
    base_url: string;
    api_key: string;
    model: string;
    temperature?: number;
    max_tokens?: number;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put('/ai-config', config);
    return response.data;
  },

  test: async (config: {
    provider: string;
    base_url: string;
    api_key: string;
    model: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/ai-config/test', config);
    return response.data;
  },
};

// --- AI Suggestions API ------------------------------------------------------

export const aiSuggestionsApi = {
  getUploadSuggestion: async (data: {
    upload_type: string;
    error_message: string;
    warnings?: string[];
    file_name?: string;
  }): Promise<{ suggestion: string; model_used: string }> => {
    const response = await apiClient.post('/ai-suggestions/upload-error', data);
    return response.data;
  },
};

// --- Journals API -----------------------------------------------------------

export const journalsApi = {
  list: async (params?: {
    period_id?: string;
    entity_id?: string;
    entry_type?: string;
    status?: string;
    source_skill?: string;
    is_manual?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<any>> => {
    const response = await apiClient.get<PaginatedResponse<any>>('/journals', { params });
    return response.data;
  },

  get: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/journals/${id}`);
    return response.data;
  },

  create: async (data: {
    period_id: number;
    entry_type?: string;
    description: string;
    reference?: string;
    lines: Array<{
      entity_id: number;
      account_id: number;
      debit: number;
      credit: number;
      currency?: string;
      description?: string;
    }>;
  }): Promise<any> => {
    const response = await apiClient.post('/journals', data);
    return response.data;
  },

  update: async (id: string, data: {
    description?: string;
    reference?: string;
    entry_type?: string;
    lines?: Array<{
      entity_id: number;
      account_id: number;
      debit: number;
      credit: number;
      currency?: string;
      description?: string;
    }>;
  }): Promise<any> => {
    const response = await apiClient.put(`/journals/${id}`, data);
    return response.data;
  },

  post: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/journals/${id}/post`);
    return response.data;
  },

  reverse: async (id: string): Promise<any> => {
    const response = await apiClient.post(`/journals/${id}/reverse`);
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await apiClient.delete(`/journals/${id}`);
    return response.data;
  },

  bulkDelete: async (params: {
    source_skill?: string;
    period_id?: string;
    entry_type?: string;
    status?: string;
  }): Promise<any> => {
    const response = await apiClient.delete('/journals', { params });
    return response.data;
  },

  bulkUpload: async (file: File, periodId?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = {};
    if (periodId) params.period_id = periodId;
    const response = await apiClient.post('/journals/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return response.data;
  },

  exportExcel: async (params?: {
    period_id?: string;
    entry_type?: string;
    status?: string;
  }): Promise<void> => {
    const response = await apiClient.get('/journals/export/excel', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `journal_entries_${new Date().toISOString().slice(0, 10)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- Export Jobs API ---------------------------------------------------------

export const exportJobsApi = {
  trigger: async (jobType: string, year: number): Promise<any> => {
    const response = await apiClient.post('/export-jobs/trigger', null, {
      params: { job_type: jobType, year },
    });
    return response.data;
  },

  status: async (jobType: string, year: number): Promise<any> => {
    const response = await apiClient.get('/export-jobs/status', {
      params: { job_type: jobType, year },
    });
    return response.data;
  },

  download: async (jobType: string, year: number): Promise<void> => {
    const response = await apiClient.get('/export-jobs/download', {
      params: { job_type: jobType, year },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="(.+?)"/)?.[1] || `export_${jobType}_${year}.xlsx`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  list: async (): Promise<any> => {
    const response = await apiClient.get('/export-jobs');
    return response.data;
  },
};

export default apiClient;
